import React, { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    // Basic validation
    if (!email) {
      setError('Please enter your email address');
      setLoading(false);
      return;
    }

    if (!/\S+@\S+\.\S+/.test(email)) {
      setError('Please enter a valid email address');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post('https://phone-tracker-backend-wqeb.onrender.com/api/password/forgot-password', { email });
      
      setMessage('Password reset email sent! Please check your inbox.');
      setSubmitted(true);
      
      // In development, show the reset token (remove in production)
      if (process.env.NODE_ENV === 'development' && response.data.resetToken) {
        setMessage(`Password reset email sent! Token: ${response.data.resetToken} (Development only)`);
      }
    } catch (err) {
      console.error('Forgot password error:', err);
      if (err.response) {
        setError(err.response.data?.message || 'Failed to send reset email');
      } else if (err.request) {
        setError('Cannot connect to server. Please check your connection.');
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      {/* Background decoration */}
      <div style={styles.backgroundDecoration}>
        <div style={styles.decorationCircle1}></div>
        <div style={styles.decorationCircle2}></div>
      </div>

      {/* Forgot Password Card */}
      <div style={styles.card}>
        {/* Header */}
        <div style={styles.header}>
          <Link to="/login" style={styles.backLink}>
            ← Back to Login
          </Link>
          <div style={styles.logo}>
            <span style={styles.logoIcon}>🔐</span>
          </div>
          <h2 style={styles.title}>Forgot Password?</h2>
          <p style={styles.subtitle}>
            Enter your email address and we'll send you instructions to reset your password.
          </p>
        </div>

        {/* Success Message */}
        {message && (
          <div style={styles.successContainer}>
            <span style={styles.successIcon}>✅</span>
            <p style={styles.successText}>{message}</p>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div style={styles.errorContainer}>
            <span style={styles.errorIcon}>⚠️</span>
            <p style={styles.errorText}>{error}</p>
          </div>
        )}

        {/* Form */}
        {!submitted ? (
          <form onSubmit={handleSubmit} style={styles.form}>
            {/* Email Field */}
            <div style={styles.inputGroup}>
              <label style={styles.label}>
                <span style={styles.labelIcon}>📧</span>
                Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                style={styles.input}
                placeholder="Enter your email"
                disabled={loading}
              />
            </div>

            {/* Submit Button */}
            <button 
              type="submit" 
              style={{
                ...styles.button,
                ...(loading ? styles.buttonDisabled : {})
              }}
              disabled={loading}
            >
              {loading ? (
                <span style={styles.loadingSpinner}>
                  <span style={styles.spinner}></span>
                  Sending...
                </span>
              ) : (
                'Send Reset Instructions'
              )}
            </button>
          </form>
        ) : (
          <div style={styles.resendContainer}>
            <p style={styles.resendText}>Didn't receive the email?</p>
            <button 
              onClick={() => setSubmitted(false)} 
              style={styles.resendButton}
              disabled={loading}
            >
              Try again
            </button>
          </div>
        )}

        {/* Help Text */}
        <div style={styles.helpContainer}>
          <p style={styles.helpText}>
            Remember your password?{' '}
            <Link to="/login" style={styles.helpLink}>
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    position: 'relative',
    overflow: 'hidden',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
  backgroundDecoration: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    zIndex: 1,
  },
  decorationCircle1: {
    position: 'absolute',
    width: '300px',
    height: '300px',
    borderRadius: '50%',
    background: 'rgba(255, 255, 255, 0.1)',
    top: '-100px',
    right: '-100px',
  },
  decorationCircle2: {
    position: 'absolute',
    width: '200px',
    height: '200px',
    borderRadius: '50%',
    background: 'rgba(255, 255, 255, 0.1)',
    bottom: '-50px',
    left: '-50px',
  },
  card: {
    position: 'relative',
    zIndex: 2,
    width: '450px',
    background: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(10px)',
    borderRadius: '20px',
    padding: '40px',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
  },
  header: {
    textAlign: 'center',
    marginBottom: '30px',
  },
  backLink: {
    display: 'inline-block',
    color: '#667eea',
    textDecoration: 'none',
    fontSize: '14px',
    marginBottom: '20px',
    ':hover': {
      textDecoration: 'underline',
    },
  },
  logo: {
    marginBottom: '20px',
  },
  logoIcon: {
    fontSize: '48px',
  },
  title: {
    fontSize: '24px',
    color: '#333',
    marginBottom: '10px',
    fontWeight: '600',
  },
  subtitle: {
    color: '#666',
    fontSize: '14px',
    lineHeight: '1.5',
  },
  successContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    backgroundColor: '#e8f5e9',
    border: '1px solid #c8e6c9',
    borderRadius: '10px',
    padding: '12px',
    marginBottom: '20px',
  },
  successIcon: {
    fontSize: '20px',
  },
  successText: {
    color: '#2e7d32',
    fontSize: '14px',
    margin: 0,
    flex: 1,
  },
  errorContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    backgroundColor: '#fee',
    border: '1px solid #fcc',
    borderRadius: '10px',
    padding: '12px',
    marginBottom: '20px',
  },
  errorIcon: {
    fontSize: '20px',
  },
  errorText: {
    color: '#c00',
    fontSize: '14px',
    margin: 0,
    flex: 1,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  label: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#555',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  labelIcon: {
    fontSize: '16px',
  },
  input: {
    padding: '12px 16px',
    border: '2px solid #e0e0e0',
    borderRadius: '10px',
    fontSize: '15px',
    transition: 'all 0.3s',
    outline: 'none',
  },
  button: {
    padding: '14px',
    background: 'linear-gradient(45deg, #667eea, #764ba2)',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s',
    marginTop: '10px',
  },
  buttonDisabled: {
    opacity: 0.7,
    cursor: 'not-allowed',
  },
  loadingSpinner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
  },
  spinner: {
    width: '20px',
    height: '20px',
    border: '3px solid rgba(255, 255, 255, 0.3)',
    borderTop: '3px solid white',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    display: 'inline-block',
  },
  resendContainer: {
    textAlign: 'center',
    padding: '20px',
  },
  resendText: {
    color: '#666',
    fontSize: '14px',
    marginBottom: '10px',
  },
  resendButton: {
    background: 'none',
    border: 'none',
    color: '#667eea',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    textDecoration: 'underline',
  },
  helpContainer: {
    marginTop: '25px',
    paddingTop: '20px',
    borderTop: '1px solid #e0e0e0',
    textAlign: 'center',
  },
  helpText: {
    color: '#666',
    fontSize: '14px',
  },
  helpLink: {
    color: '#667eea',
    textDecoration: 'none',
    fontWeight: '600',
    marginLeft: '5px',
    ':hover': {
      textDecoration: 'underline',
    },
  },
};

export default ForgotPassword;